import Api           from '@/helpers/Api.js';
import _             from 'underscore';
import qs            from 'qs';
import moment        from 'moment';
import Alert         from '@/helpers/Alert.js';
import i18n          from '@/libraries/I18n.js';
import Vue           from 'vue';
import jsonNormalize from '@lysyi3m/json-api-normalizer';

// Import mutations
import {
    SET_CONVERSATION,
    APPEND_CONVERSATIONS,
    NEW_MESSAGE_EVENT,
    NEW_CURRENT_CONTACT_STATUS_UPDATED,
    NEW_CONTACT_STATUS_UPDATED,
    SET_ACTIVE_CONTACT_ID,
    TERMINATE_CONVERSATION,
    SET_SINGLE_CONTACT_CONVERSATIONS,
    SET_SINGLE_CONTACT_ACTIVE_CONVERSATION,
    RESET_SINGLE_CONTACT_CONVERSATION,
    SET_CONVERSATION_MESSAGES,
    APPEND_CONVERSATIONS_MESSAGES,
    APPEND_CURRENT_CONVERSATION_WITH_NEW_MESSAGE,
    APPEND_SINGLE_CONTACT_CONVERSATIONS
} from './mutation-types.js';

const resource = '/connectors/sms/v1';

const filtredConversations = {
    'current-user': 'currentUserConversations',
    'other-users':  'otherUsersConversations',
    'no-users':     'noUsersConversations'
};

// Initial state
const state = () => ({
    /**
     *  -----------------------------------------------------
     * | All conversations section (filtred with related to) |
     *  -----------------------------------------------------
     */

    currentUserConversations: {
        conversations: [],
        included:      [],
        meta:          []
    },
    otherUsersConversations: {
        conversations: [],
        included:      [],
        meta:          []
    },
    noUsersConversations: {
        conversations: [],
        included:      [],
        meta:          []
    },

    // Websockets new events
    newMessage:                 0,
    currentContactStatuUpdated: 0,
    contactStatuUpdated:        0,

    /**
     *  ---------------------------------------------------
     * | SMS Chat and Current contact conversation section |
     *  ---------------------------------------------------
     */

    activeContactId:           null,
    contactActiveConversation: null,
    contactHasConversations:   false,
    contactConversations:      { conversations: {} }
});

// Getters
const getters = {
    /**
     *  -----------------------------------------------------
     * | All conversations section (filtred with related to) |
     *  -----------------------------------------------------
     */

    /**
     * Get the included contacts.
     *
     * @param      {Object}  state   The state
     *
     * @return     {Object}
     */
    convoContacts: (state) => {
        const totalIncluded = [...state.currentUserConversations.included, ...state.otherUsersConversations.included, ...state.noUsersConversations.included];
        return totalIncluded.filter(include => include.type  === 'contacts') ? totalIncluded.filter(include => include.type  === 'contacts') : [];
    },

    /**
     * Get the included messages.
     *
     * @param      {Object}  state   The state
     *
     * @return     {Object}
     */
    convoMessages: (state) => {
        const totalIncluded = [...state.currentUserConversations.included, ...state.otherUsersConversations.included, ...state.noUsersConversations.included];
        return totalIncluded.filter(include => include.type  === 'messages') ? totalIncluded.filter(include => include.type  === 'messages') : [];
    },

    /**
     * Get the included users.
     *
     * @param      {Object}  state   The state
     *
     * @return     {Object}
     */
    convoUsers: (state) => {
        const totalIncluded = [...state.currentUserConversations.included, ...state.otherUsersConversations.included, ...state.noUsersConversations.included];
        return totalIncluded.filter(include => include.type  === 'users') ? totalIncluded.filter(include => include.type  === 'users') : [];
    },

    /**
     *  ---------------------------------------------------
     * | SMS Chat and Current contact conversation section |
     *  ---------------------------------------------------
     */

    /**
     * Tells if the current contact is subscribed or not to conversations
     *
     * @param      {Object}  state   The state
     *
     * @return     {Boolean}
     *
     */
    isSubscribed: (state) => {
        const conversation = Object.values(state.contactConversations.conversations).find(conversation => conversation.contact);
        if (conversation) {
            return conversation.contact['is-subscribed'];
        }
        return true;
    },

    /**
     * Check if the current user can chat with the current contact,
     * only if the active conversation is assigned to the current user or if it's userless
     *
     * @param      {Object}  state     The state
     * @param      {Object}  getters   The getters
     * @param      {Object}  rootState The state
     *
     * @return     {Boolean}
     */
    currentUserCanChatWithContact: (state, getters, rootState) => {
        if (!rootState.core.auth.user) {
            return false;
        }

        return (state.contactActiveConversation !== null && state.contactActiveConversation['active-users'].includes(rootState.core.auth.user.id)) ||
            state.contactActiveConversation['active-users'].length === 0;
    },

    /**
     * Get the current conversation assigned user
     *
     * @param {object} state
     */
    currentConversationUser: (state) => {
        if (state.contactActiveConversation === null ||
            state.contactActiveConversation['active-users'].length === 0
        ) {
            return null;
        }
        const activeUserId = state.contactActiveConversation['active-users'][0];
        return (state.contactActiveConversation.users ?? []).find(user => user.id === activeUserId) ?? null;
    }
};

// Actions

const actions = {
    /**
     *  -----------------------------------------------------
     * | All conversations section (filtred with related to) |
     *  -----------------------------------------------------
     */

    /**
     * List Conversations received to number
     *
     * @param   {Object}  context  The context
     *
     * @return  {Promise}
     */
    index: (context, payload) => {
        const urlParams = { include: 'contact,users,latest-message' };
        if (payload) {
            urlParams.sort   = payload.sort ? payload.sort : void 0;
            urlParams.filter = payload.filters ? payload.filters : void 0;
            urlParams.page   = payload.page ? payload.page : void 0;
        }

        return new Promise((resolve, reject) => {
            Api.get(`${resource}/conversations`, {
                params:           urlParams,
                paramsSerializer: params => qs.stringify(params, { encode: false })
            })
                .then(response => {
                    if (payload.append === true) {
                        context.commit(APPEND_CONVERSATIONS, {
                            name:     urlParams.filter?.['related-to'],
                            data:     response.data.data,
                            included: response.data.included !== void 0 ? response.data.included : [],
                            meta:     response.data.meta !== void 0 ? response.data.meta : []
                        });

                        return resolve(response);
                    }

                    context.commit(SET_CONVERSATION, {
                        name:     urlParams.filter?.['related-to'],
                        data:     response.data.data,
                        included: response.data.included !== void 0 ? response.data.included : [],
                        meta:     response.data.meta !== void 0 ? response.data.meta : []
                    });

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    /**
     * Update Conversation include messages
     *
     * @param   {Object}  context  The context
     * @param   {String}  tenantId Id of the tenant
     *
     * @return  {Promise}
     */
    update: (context, { tenantId }) => {
        const channelName = `SMS.Connectors.Messages.${tenantId}.all`;
        const currentChannel = window.Echo.private(channelName);

        // Listen to new message received event
        currentChannel.listen('.MessageReceived', (event) => {
            context.commit(NEW_MESSAGE_EVENT);

            // If the new received message is for the active conversation, append it.
            if (event.contact['parent-id'] === context.state.activeContactId) {
                context.commit(APPEND_CURRENT_CONVERSATION_WITH_NEW_MESSAGE, {
                    conversationId: event.conversationId,
                    data:           { ...event.message, id: event.id }
                });
            }
        });

        // Listen to contact updated receiving status event
        currentChannel.listen('.ContactMessageReceivingStatusUpdated', (event) => {
            // If the current contact's status updated dispatch NEW_CURRENT_CONTACT_STATUS_UPDATED or dispatch NEW_CONTACT_STATUS_UPDATED
            if (event.contact['parent-id'] === context.state.activeContactId) {
                context.commit(NEW_CURRENT_CONTACT_STATUS_UPDATED);
            } else {
                context.commit(NEW_CONTACT_STATUS_UPDATED);
            }
        });
    },

    /**
     * Stop listening to websockets when we don't need it
     *
     * @param   {String}  tenantId Id of the tenant
     *
     * @return  void
     */
    stopListeningToWebsocket: (context, tenantId) => {
        const channelName = `SMS.Connectors.Messages.${tenantId}.all`;
        const currentChannel = window.Echo.private(channelName);
        currentChannel.stopListening('.MessageReceived');
        currentChannel.stopListening('.ContactMessageReceivingStatusUpdated');
    },

    /**
     * Set the active conversation contact
     */
    setActiveContact: (context, contactId) => {
        context.commit(SET_ACTIVE_CONTACT_ID, contactId);
    },

    /**
     * Terminate a conversation
     *
     * @param      {Object}             context     The context
     * @param      {string|undefined}   payload.id  The conversation identifier
     *
     * @return     {Promise}
     */
    terminateConversation: (context, { id }) => new Promise((resolve, reject) => {
        Api.patch(`${resource}/conversations/${id}?include=users`, {
            'data': {
                'type':       'conversations',
                id,
                'attributes': { 'ended_at': moment().format() }
            }
        })
            .then((response) => {
                context.commit(TERMINATE_CONVERSATION, {
                    id,
                    conversation: jsonNormalize(response.data, { camelizeKeys: false }).data
                });
                Alert.success(i18n.t('conversation-terminated'));
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            });
    }),

    /**
     * Assign a conversation to a specific User
     *
     * @param      {Object}   context         The context
     * @param      {string}   conversationId  The conversation identifier
     * @param      {string}   userId          The user identifier
     *
     * @return     {Promise}
     */
    assignConversation: (context, { conversationId, userId }) => new Promise((resolve, reject) => {
        Api.patch(`${resource}/conversations/${conversationId}/assign`, {
            'data': {
                'type':       'conversations',
                'id':         conversationId,
                'attributes': { 'user_id': userId }
            }
        })
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            });
    }),

    /**
     * Unassign a conversation to a specific User
     *
     * @param      {Object}   context         The context
     * @param      {string}   conversationId  The conversation identifier
     * @param      {string}   userId          The user identifier
     *
     * @return     {Promise}
     */
    unassignConversation: (context, { conversationId, userId }) => new Promise((resolve, reject) => {
        Api.patch(`${resource}/conversations/${conversationId}/unassign`, {
            'data': {
                'type':       'conversations',
                'id':         conversationId,
                'attributes': { 'user_id': userId }
            }
        })
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            });
    }),

    /**
     * Get messages for a specific conversation
     *
     * @param      {Object}             context                  The context
     * @param      {string}   payload.conversationId   The conversation identifier
     * @param      {boolean}  payload.append           Whether to append messages or not
     *
     * @return     {Promise}
     */
    getConversationMessages: (context, { conversationId, append, page }) => {
        const urlParams = {};
        if (page) {
            urlParams.page = page;
            urlParams.include = 'media';
        }

        return new Promise((resolve, reject) => {
            Api.get(`${resource}/conversations/${conversationId}/messages`, {
                params: {
                    sort: '-created-at',
                    ...urlParams
                },
                paramsSerializer: params => qs.stringify(params, { encode: false })
            })
                .then((response) => {
                    const messages = jsonNormalize(response.data, { camelizeKeys: false }).data;

                    if (append) {
                        context.commit(APPEND_CONVERSATIONS_MESSAGES, {
                            conversationId,
                            data: messages
                        });
                    } else {
                        context.commit(SET_CONVERSATION_MESSAGES, {
                            conversationId,
                            data: messages
                        });
                    }

                    resolve({
                        data:     messages,
                        included: response.data.included ?? {},
                        meta:     response.data.meta ?? {}
                    });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    /**
     *  ---------------------------------------------------
     * | SMS Chat and Current contact conversation section |
     *  ---------------------------------------------------
     */

    /**
     * Get single contact's conversations.
     *
     * @param      {Object}             context     The context
     * @param      {string|undefined}   payload.id  The contact identifier
     *
     * @return     {Promise}
     */
    getContactConversations: (context, { id, filters, append, page }) => new Promise((resolve, reject) => {
        /**
         * Either get the passed contact identifier
         * or the state active Contact Id
         */
        const contactId = id || context.state.activeContactId;

        const urlParams = {};
        if (page) {
            urlParams.page = page;
        }

        Api.get(`${resource}/contacts/${contactId}/conversations`, {
            params: {
                filter:  filters,
                include: 'contact,users,latest-message',
                sort:    '-created_at',
                ...urlParams
            },
            paramsSerializer: params => qs.stringify(params, { encode: false })
        })
            .then((response) => {
                const jsonNormalizedData = jsonNormalize(response.data, { camelizeKeys: false });

                // Index the flat conversations by id so the message mutations can reach them.
                const conversationsById = (jsonNormalizedData.data ?? []).reduce((map, conversation) => {
                    map[conversation.id] = conversation;
                    return map;
                }, {});

                if (append) {
                    context.commit(APPEND_SINGLE_CONTACT_CONVERSATIONS, { conversations: conversationsById });
                } else {
                    context.commit(SET_SINGLE_CONTACT_CONVERSATIONS, { conversations: conversationsById });
                }

                // If the first conversation (page 1) is active we can set it to contactActiveConversation using SET_SINGLE_CONTACT_ACTIVE_CONVERSATION
                if (filters?.['is-active'] || page?.number === 1) {
                    // Set active conversations
                    context.commit(SET_SINGLE_CONTACT_ACTIVE_CONVERSATION, null);
                    for (const conversation of jsonNormalizedData.data ?? []) {
                        if (conversation['is-active']) {
                            context.commit(SET_SINGLE_CONTACT_ACTIVE_CONVERSATION, conversation);
                            break;
                        }
                    }
                }

                resolve({
                    data:     { conversations: conversationsById },
                    included: response.data.included ?? {},
                    meta:     response.data.meta ?? {}
                });
            })
            .catch((error) => {
                context.commit(SET_SINGLE_CONTACT_CONVERSATIONS, { conversations: [] });
                reject(error);
            });
    }),

    /**
     * Reset state.singleConversation to null
     */
    resetSingleConversation (context) {
        context.commit(RESET_SINGLE_CONTACT_CONVERSATION);
    }
};

// Mutations
const mutations = {
    [SET_CONVERSATION]: (state, {
        name, data, included, meta
    }) => {
        Vue.set(state[filtredConversations[name]], 'conversations', data);
        Vue.set(state[filtredConversations[name]], 'included', included);
        Vue.set(state[filtredConversations[name]], 'meta', meta);
    },
    [APPEND_CONVERSATIONS]: (state, {
        name, data, included, meta
    }) => {
        Vue.set(state[filtredConversations[name]], 'conversations', state[filtredConversations[name]].conversations.concat(data));
        Vue.set(state[filtredConversations[name]], 'included', state[filtredConversations[name]].included.concat(included));
        Vue.set(state[filtredConversations[name]], 'meta', meta);
    },
    [SET_CONVERSATION_MESSAGES]: (state, { conversationId, data }) => {
        /* eslint-disable no-magic-numbers */
        const currentUserConversation = state.currentUserConversations.conversations.findIndex(conversation => conversation.id === conversationId);
        const otherUsersConversation = state.otherUsersConversations.conversations.findIndex(conversation => conversation.id === conversationId);
        const noUsersConversation = state.noUsersConversations.conversations.findIndex(conversation => conversation.id === conversationId);

        if (currentUserConversation !== -1) {
            Vue.set(state.currentUserConversations.conversations[currentUserConversation], 'messages', data);
        }

        if (otherUsersConversation !== -1) {
            Vue.set(state.otherUsersConversations.conversations[otherUsersConversation], 'messages', data);
        }

        if (noUsersConversation !== -1) {
            Vue.set(state.noUsersConversations.conversations[noUsersConversation], 'messages', data);
        }

        if (state.contactConversations !== undefined && Object.keys(state.contactConversations.conversations).length) {
            Vue.set(state.contactConversations.conversations[conversationId], 'messages', data);
        }
    },
    [APPEND_CONVERSATIONS_MESSAGES]: (state, { conversationId, data }) => {
        const currentUserConversation = state.currentUserConversations.conversations.findIndex(conversation => conversation.id === conversationId);
        const otherUsersConversation = state.otherUsersConversations.conversations.findIndex(conversation => conversation.id === conversationId);
        const noUsersConversation = state.noUsersConversations.conversations.findIndex(conversation => conversation.id === conversationId);

        if (currentUserConversation !== -1) {
            const messages = state.currentUserConversations.conversations[currentUserConversation]?.messages || [];
            Vue.set(state.currentUserConversations.conversations[currentUserConversation], 'messages', [...messages, ...data]);
        }

        if (otherUsersConversation !== -1) {
            const messages = state.otherUsersConversations.conversations[otherUsersConversation]?.messages || [];
            Vue.set(state.otherUsersConversations.conversations[otherUsersConversation], 'messages', [...messages, ...data]);
        }

        if (noUsersConversation !== -1) {
            const messages = state.noUsersConversations.conversations[noUsersConversation]?.messages || [];
            Vue.set(state.noUsersConversations.conversations[noUsersConversation], 'messages', [...messages, ...data]);
        }

        if (state.contactConversations !== undefined && Object.keys(state.contactConversations.conversations).length) {
            const messages = state.contactConversations.conversations[conversationId]?.messages || [];
            Vue.set(state.contactConversations.conversations[conversationId], 'messages', [...messages, ...data]);
        }
    },
    [APPEND_CURRENT_CONVERSATION_WITH_NEW_MESSAGE]: (state, { conversationId, data }) => {
        if (state.contactConversations !== undefined && Object.keys(state.contactConversations.conversations).length) {
            const messages = state.contactConversations.conversations[conversationId]?.messages || [];
            Vue.set(state.contactConversations.conversations[conversationId], 'messages', [data, ...messages]);
        }
    },
    [TERMINATE_CONVERSATION]: (state, { id, conversation }) => {
        Object.values(filtredConversations).forEach(list => {
            const index = state[list].conversations.findIndex((item) => item.id === id);
            if (index !== -1) {
                state[list].conversations = _.reject(state[list].conversations, (item) => item.id === id);
                Vue.set(state[list].meta.page, 'total', state[list].meta.page.total - 1);
            }
        });

        // Mark the conversation as ended in the contact history, so the end message is shown
        const contactConversation = state.contactConversations.conversations[id];
        if (contactConversation) {
            Vue.set(state.contactConversations.conversations, id, {
                ...contactConversation,
                'is-active':  conversation['is-active'],
                'ended-at':   conversation['ended-at'],
                'updated-at': conversation['updated-at'],
                'users':      conversation.users ?? contactConversation.users
            });
        }

        state.contactActiveConversation = null;
    },
    [SET_ACTIVE_CONTACT_ID]: (state, { contactId }) => {
        state.activeContactId = contactId;
    },
    [NEW_MESSAGE_EVENT]: (state) => {
        state.newMessage++;
    },
    [NEW_CURRENT_CONTACT_STATUS_UPDATED]: (state) => {
        state.currentContactStatuUpdated++;
    },
    [NEW_CONTACT_STATUS_UPDATED]: (state) => {
        state.contactStatuUpdated++;
    },

    /**
     *  ---------------------------------------------------
     * | SMS Chat and Current contact conversation section |
     *  ---------------------------------------------------
     */

    [SET_SINGLE_CONTACT_CONVERSATIONS]: (state, payload) => {
        state.contactConversations = { conversations: {} };
        state.contactHasConversations = false;

        if (payload.conversations && Object.values(payload.conversations).length) {
            state.contactConversations = payload;
            state.contactHasConversations = true;
        }
    },
    [APPEND_SINGLE_CONTACT_CONVERSATIONS]: (state, payload) => {
        if (payload.conversations && Object.values(payload.conversations).length) {
            // Existing entries win, and the literal keeps the payload unmutated
            Vue.set(state.contactConversations, 'conversations', Object.assign({}, payload.conversations, state.contactConversations.conversations));
        }
    },
    [SET_SINGLE_CONTACT_ACTIVE_CONVERSATION]: (state, payload) => {
        state.contactActiveConversation = payload;
    },
    [RESET_SINGLE_CONTACT_CONVERSATION]: (state) => {
        state.contactConversations = { conversations: {} };
        state.contactActiveConversation = null;
        state.contactHasConversations = false;
    }
};

// Export module
export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
